/**
 * GeneratedReportsHistory — compact audit-trail list of previously generated
 * patient report PDFs (clinics/{id}/generatedReports), with re-download links.
 *
 * Rows that carry their editable source (`editable` + `contentHtml` — the
 * "סיכום טיפולים AI" type) also get an edit action that opens the AI summary
 * page (/dashboard/patients/:pid/ai-summary?reportId=…) seeded with the last
 * exported text, for editing + re-export as a new revision.
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ClockCounterClockwise, FilePdf, PencilSimpleLine } from "@phosphor-icons/react";
import {
  selectTherapistId,
  selectIsAdmin,
} from "../../../../store/slices/authSlice";
import { useGetGeneratedReportsQuery } from "../../../../store/services/patientReportsApi";
import { formatHebrewDate } from "../../../../utils/dateUtils";

const isEditable = (r) =>
  r?.editable === true &&
  r.reportTypeId === "aiTreatmentsSummary" &&
  !!r.patientId &&
  typeof r.contentHtml === "string" &&
  r.contentHtml.trim();

function GeneratedReportsHistory({ clinicId, scopeTherapistId, canEdit = false }) {
  const therapistId = useSelector(selectTherapistId);
  const isAdmin = useSelector(selectIsAdmin);
  const navigate = useNavigate();

  // Non-admins may only read rows they created (firestore.rules), so the
  // query itself must be scoped — rules are not filters.
  const { data: reports } = useGetGeneratedReportsQuery(
    { clinicId, createdBy: isAdmin ? null : therapistId },
    { skip: !clinicId || (!isAdmin && !therapistId) }
  );

  const visible = useMemo(() => {
    if (!reports) return [];
    // Non-admins see only their own generated reports; admins follow the
    // page-level therapist scope selector when one is active.
    if (!isAdmin) return reports.filter((r) => r.createdBy === therapistId);
    if (scopeTherapistId)
      return reports.filter((r) => r.createdBy === scopeTherapistId);
    return reports;
  }, [reports, isAdmin, therapistId, scopeTherapistId]);

  if (visible.length === 0) return null;

  return (
    <div className="prw-history">
      <div className="prw-history-title">
        <ClockCounterClockwise size={15} weight="bold" />
        דוחות שהופקו לאחרונה
      </div>
      <div className="prw-history-list">
        {visible.map((r) => {
          const editable = canEdit && isEditable(r);
          const revision = Number(r.revision) || 1;
          return (
            <div key={r.id} className="prw-history-row">
              <span className="prw-history-main">
                <span className="prw-history-type">
                  {r.reportTypeLabel}
                  {r.title ? ` · ${r.title}` : ""}
                  {revision > 1 && (
                    <span className="prw-history-rev" title="גרסה מעודכנת של דוח קודם">
                      גרסה {revision}
                    </span>
                  )}
                </span>
                <span className="prw-history-meta">
                  {r.patientName} · {formatHebrewDate(r.rangeStart)} –{" "}
                  {formatHebrewDate(r.rangeEnd)}
                  {r.createdAt ? ` · הופק ב-${formatHebrewDate(r.createdAt)}` : ""}
                  {r.createdByName ? ` · ${r.createdByName}` : ""}
                </span>
              </span>
              <span className="prw-history-actions">
                {editable && (
                  <button
                    type="button"
                    className="prw-history-download prw-history-edit"
                    onClick={() =>
                      navigate(`/dashboard/patients/${r.patientId}/ai-summary?reportId=${r.id}`)
                    }
                    title="עריכה וייצוא מחדש בעמוד הסיכום"
                    aria-label={`עריכת דוח ${r.reportTypeLabel} של ${r.patientName}`}
                  >
                    <PencilSimpleLine size={16} weight="bold" />
                  </button>
                )}
                <a
                  className="prw-history-download"
                  href={r.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="פתיחת ה-PDF"
                  aria-label={`פתיחת דוח ${r.reportTypeLabel} של ${r.patientName}`}
                >
                  <FilePdf size={16} weight="fill" />
                </a>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default GeneratedReportsHistory;
